import { isCricketTieOrDrawSelectionName } from "../../../utils/cricketTestMatch";
import { formatCountdownDuration } from "../../../utils/matchCountdown";
import { parseCricketMatchName } from "../../Games/matchUtils";
import { formatCricketCombinedInningsScore } from "../../common/scoreboardOutcomeAnimation";
import {
    cricketLiveOverNumberFromCurBall,
    isSquaresAtInningsOversZero,
} from "../../Cricket/cricketMatchCardOverDisplay";
import { bullionTeamAbbreviation } from "./bullionTeamAbbreviation";
import { resolveCricketTeamNameForLogo } from "../../shared/logos/cricketTeamLogoResolve";

export function teamScoresFromStatus(status?: string): [string, string] {
    const values = String(status || "").match(/\d+\/\d+/g) || [];
    return [values[0] || "--", values[1] || "--"];
}

export function normalizeMatchStatus(raw?: string): "SCHEDULED" | "IN-PLAY" | "BREAK IN PLAY" | "FINISHED" {
    const s = String(raw || "").toUpperCase();
    if (
        s.includes("MATCH COMPLETE") ||
        s.includes("EVENT CLOSED") ||
        s.includes("MATCH FINISHED") ||
        (s.includes("FINISHED") && !s.includes("NOT FINISHED"))
    ) {
        return "FINISHED";
    }
    if (
        s.includes("IN-PLAY") ||
        s.includes("MATCH IN PROGRESS") ||
        /^\d+/.test(s)
    ) {
        return "IN-PLAY";
    }
    if (
        s.includes("BREAK IN PLAY") ||
        s.includes("DRINKS") ||
        s.includes("RAIN DELAY") ||
        s.includes("BAD WEATHER") ||
        s.includes("WEATHER DELAY") ||
        s.includes("TOSS DELAYED") ||
        s.includes("WET OUTFIELD")
    ) {
        return "BREAK IN PLAY";
    }
    return "SCHEDULED";
}

export function formatBullionCountdown(ms: number): string {
    return formatCountdownDuration(ms) ?? "1m";
}

export function formatBullionStartsAtDateTime(ts: number): string {
    const d = new Date(ts);
    if (!Number.isFinite(d.getTime())) return "SCHEDULED";
    return `Starts ${d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    })}`;
}

export function isBullionMarketRunnerTie(runnerName: string | undefined | null): boolean {
    return isCricketTieOrDrawSelectionName(runnerName);
}

function formatBullionHandicapLine(val: unknown): string | null {
    const n = typeof val === "number" ? val : parseFloat(String(val ?? "").trim());
    if (!Number.isFinite(n) || n <= 0) return null;
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/**
 * Bare "Over" / "Under" from DB or snapshot → "Over 12.5" when `line` is known (room board uses the same rule).
 */
export function bullionRunnerDisplayNameWithLine(
    runnerName: string | undefined | null,
    line: number | null | undefined
): string {
    const raw = String(runnerName ?? "").trim();
    if (!raw) return "—";
    const low = raw.toLowerCase();
    if (/\b(?:over|under)\s+\d/.test(low)) return raw;
    const fmt = formatBullionHandicapLine(line);
    if (!fmt) return raw;
    if (low === "over") return `Over ${fmt}`;
    if (low === "under") return `Under ${fmt}`;
    return raw;
}

/**
 * Markets that should show a player headshot on the slip (aligned with Trader `market-display.jsx`,
 * plus BULLION player-tab type IDs: batter runs / batter sixes (710/910/730/914).
 */
export function bullionMarketShowsPlayerImage(meta: {
    marketTypeId?: string | null;
    marketType?: string | null;
    marketName?: string | null;
}): boolean {
    const idRaw = String(meta.marketTypeId ?? "").trim();
    const id = /^\d+(\.0+)?$/.test(idRaw) ? String(parseInt(idRaw, 10)) : idRaw;
    const mt = String(meta.marketType ?? "").trim();
    const marketTypeLower = mt.toLowerCase();
    const name = String(meta.marketName ?? "").toLowerCase();

    if (mt === "Next Player Out") return true;
    if (
        id === "270" ||
        id === "940" ||
        id === "280" ||
        id === "950" ||
        id === "710" ||
        id === "730" ||
        id === "910" ||
        id === "914"
    ) {
        return true;
    }
    if (
        marketTypeLower.includes("top batter") ||
        marketTypeLower.includes("top batsman") ||
        marketTypeLower.includes("top bowler") ||
        marketTypeLower.includes("batter runs") ||
        marketTypeLower.includes("batter sixes") ||
        marketTypeLower.includes("batsman sixes") ||
        marketTypeLower.includes("batter match") ||
        marketTypeLower.includes("bowler match") ||
        marketTypeLower.includes("player of the") ||
        marketTypeLower.includes("man of the") ||
        marketTypeLower.includes("player performance")
    ) {
        return true;
    }
    if (name.includes("next player out")) return true;
    if (name.includes("top batter") || name.includes("top batsman") || name.includes("top bowler")) return true;
    if (name.includes("batter runs") || name.includes("batter sixes") || name.includes("batsman sixes")) return true;
    if (name.includes("player of the") || name.includes("man of the")) return true;
    if (/\bplayer\s+to\s+score\b/i.test(String(meta.marketName ?? ""))) return true;
    if (name.includes("to reach") && name.includes("runs")) return true;
    return false;
}

export function normalizeTeamKey(value?: string): string {
    return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

/**
 * Scorecard `bat_now` vs fixture row labels often differ (“NZ” vs “New Zealand”).
 * Matches strict equality first, then normalized substring equality (same idea as slip-side inference).
 */
export function bullionBattingLabelMatchesFixtureTeam(batNowLabel: string | undefined | null, fixtureTeamLabel: string): boolean {
    const bat = String(batNowLabel ?? "").trim();
    const row = String(fixtureTeamLabel ?? "").trim();
    if (!bat || !row) return false;
    if (bat === row) return true;
    const bKey = normalizeTeamKey(bat);
    const rKey = normalizeTeamKey(row);
    if (!bKey || !rKey) return false;
    if (bKey === rKey) return true;
    return bKey.includes(rKey) || rKey.includes(bKey);
}

/** Same logic as Trader `Cricket/match-play` — Test multi-innings: `120/3 & 45/2`. */
export function formatTraderMatchPlayDisplayScore(primary: string | undefined | null, secondary: string | undefined | null): string {
    return formatCricketCombinedInningsScore(primary, secondary);
}

/** Trader `match-play.jsx` / Squares `formatStatusWithSurnames` — socket `status` “Bowler to Batsman” → surnames. */
export function formatTraderMatchPlayBallStatusCaption(statusText: string | undefined | null): string | null {
    const trimmed = String(statusText ?? "").trim();
    if (!trimmed || trimmed === "to 0" || trimmed === "to") return null;
    if (trimmed.toLowerCase().startsWith("to ")) return null;
    const toMatch = trimmed.match(/^(.+?)\s+to\s+(.+?)$/i);
    if (!toMatch) return trimmed;
    const [, bowler, batsman] = toMatch;
    const bowlerSurname = bowler.trim().split(/\s+/).pop();
    const batsmanSurname = batsman.trim().split(/\s+/).pop();
    if (bowlerSurname === "0" || batsmanSurname === "0" || !bowlerSurname || !batsmanSurname) return null;
    return `${bowlerSurname} to ${batsmanSurname}`;
}

/** Leading overs token from `match_comment` (fallback for pink OVER when innings decimal missing). */
export function parseMatchCommentLeadingOver(trimmedComment: string): { overToken: string | null; body: string } {
    const trimmed = String(trimmedComment ?? "").trim();
    if (!trimmed) return { overToken: null, body: "" };
    const withRest = trimmed.match(/^(\d+(?:\.\d+)?)\s+(.*)$/);
    if (withRest?.[1] != null && withRest[2] != null)
        return { overToken: withRest[1], body: String(withRest[2]).trim() };
    const alone = trimmed.match(/^(\d+(?:\.\d+)?)$/);
    if (alone?.[1]) return { overToken: alone[1], body: "" };
    return { overToken: null, body: trimmed };
}

/** Trader `recentDeliveries`: last 5 raw feed entries (no Squares filtration). */
export function traderMatchRecentDeliveryStrip(overDelivery: unknown[]): string[] {
    return (overDelivery || [])
        .filter((ball) => ball !== "" && ball !== null && ball !== undefined)
        .slice(-5)
        .map((b) => String(b));
}

export type BullionLiveBallStripMatchStatus = "SCHEDULED" | "IN-PLAY" | "BREAK IN PLAY" | "FINISHED";

/**
 * Mobile/desktop live OVER tab + white ball strip.
 * Hide during innings break / chase-not-started (`0.0`) so we never paint empty OVER 1 + pink 0.0.
 */
export function shouldShowBullionLiveBallStrip(
    matchStatus: BullionLiveBallStripMatchStatus,
    currentInningsOversDecimal: number | null | undefined,
    opts?: { isInningsBreak?: boolean }
): boolean {
    if (matchStatus !== "IN-PLAY" && matchStatus !== "BREAK IN PLAY") return false;
    if (opts?.isInningsBreak) return false;
    if (isSquaresAtInningsOversZero(currentInningsOversDecimal)) return false;
    return true;
}

/**
 * Trader `markets.jsx` currentOverNumber: `Math.floor(parseFloat(curBall)) + 1`, else innings completed overs + 1.
 */
export function traderMatchPlayCurrentOverOneBased(
    curBall: string | number | null | undefined,
    battingOvers: unknown,
    innsNow: number | undefined | null
): number | null {
    const fromBall = cricketLiveOverNumberFromCurBall(curBall);
    if (fromBall != null) return fromBall;
    if (!battingOvers || !Array.isArray(battingOvers) || innsNow == null || Number(innsNow) < 1) return null;
    const idx = Number(innsNow) - 1;
    if (idx >= battingOvers.length) return null;
    const completed = Number(battingOvers[idx]);
    if (!Number.isFinite(completed) || completed < 0) return null;
    return completed + 1;
}

export function inferSelectionSide(runnerName: string, marketName: string, homeTeam: string, awayTeam: string): "home" | "away" | null {
    const runnerKey = normalizeTeamKey(runnerName);
    const marketKey = normalizeTeamKey(marketName);
    const combined = `${runnerKey} ${marketKey}`;

    if (/\bhome\b/.test(combined)) return "home";
    if (/\baway\b/.test(combined)) return "away";

    const homeKey = normalizeTeamKey(homeTeam);
    const awayKey = normalizeTeamKey(awayTeam);
    if (homeKey && (runnerKey === homeKey || runnerKey.includes(homeKey) || homeKey.includes(runnerKey))) return "home";
    if (awayKey && (runnerKey === awayKey || runnerKey.includes(awayKey) || awayKey.includes(runnerKey))) return "away";

    const runRaw = String(runnerName || "").trim();
    const abbrHome = bullionTeamAbbreviation(homeTeam);
    const abbrAway = bullionTeamAbbreviation(awayTeam);
    if (abbrHome.length >= 2 && bullionAbbrevAppearsAsDistinctToken(runRaw, abbrHome) && homeKey) return "home";
    if (abbrAway.length >= 2 && bullionAbbrevAppearsAsDistinctToken(runRaw, abbrAway) && awayKey) return "away";

    return null;
}

function bullionTextMentionsTeam(text: string, team: string): boolean {
    const h = normalizeTeamKey(text);
    const t = normalizeTeamKey(team);
    if (!h || !t) return false;
    return h.includes(t) || t.includes(h);
}

/** True when `abbr` appears as its own token in `text` (e.g. "KKR" in "KKR Total Runs", not a substring of a longer word). */
function bullionAbbrevAppearsAsDistinctToken(text: string, abbr: string): boolean {
    const a = String(abbr || "").trim();
    if (a.length < 2) return false;
    let escaped: string;
    try {
        escaped = a.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    } catch {
        return false;
    }
    try {
        return new RegExp(`\\b${escaped}\\b`, "i").test(String(text || ""));
    } catch {
        return false;
    }
}

/**
 * `PlayerHeadshotImage` CDN path uses full franchise-style folder names. Scoreboard `home`/`away` are often
 * abbreviations (e.g. IND, KKR); align them to the long names from the fixture title when possible.
 */
export function bullionResolvedTeamNameForPlayerCdnFolder(fixtureSideLabel: string, parsedOrFallbackPool: string[]): string {
    return resolveCricketTeamNameForLogo(fixtureSideLabel, parsedOrFallbackPool);
}

/** Batter-style markets: `{team or abbr}, {player} Runs|Fours|…` (runner is often Over/Under only). */
const BULLION_COMMA_PLAYER_PROP_HEAD_RE = /^([^,]+),\s*(.+?)\s+(Runs|Fours|Sixes|Wickets)\b/i;

/**
 * Player headshots use `Cricket/Player Images/{slug}.png` (flat S3). Team + player live in the
 * market title (e.g. `PK, Cooper Connolly Runs 22.5` or `Delhi Capitals, Axar Patel Bullion 1.5`).
 */
export function bullionParseCommaPrefixedPlayerPropForCdn(
    marketName: string,
    teamA: string,
    teamB: string,
    roomMatchName: string
): { folderTeam: string; playerName: string } | null {
    const raw = String(marketName || "").trim();
    const m = raw.match(BULLION_COMMA_PLAYER_PROP_HEAD_RE);
    if (!m) return null;
    const teamToken = String(m[1] || "").trim();
    const playerPart = String(m[2] || "").trim();
    if (!teamToken || !playerPart) return null;
    const pt = parseCricketMatchName(roomMatchName || "").teams;
    const pool = pt.length >= 2 ? pt : [teamA, teamB];
    const folderTeam = bullionResolvedTeamNameForPlayerCdnFolder(teamToken, pool);
    return { folderTeam, playerName: playerPart };
}

/**
 * Fixture-order teams referenced in the slip line: full names or common short codes
 * (from `bullionTeamAbbreviation`) in either the market title or the selection name.
 */
function teamsMentionedInBullionSlipContext(marketName: string, runnerName: string, homeTeam: string, awayTeam: string): string[] {
    const out: string[] = [];
    const keys = new Set<string>();
    const combined = `${String(marketName || "")} ${String(runnerName || "")}`.trim();

    for (const team of [homeTeam, awayTeam]) {
        const nk = normalizeTeamKey(team);
        if (!nk || keys.has(nk)) continue;
        if (bullionTextMentionsTeam(combined, team)) {
            keys.add(nk);
            out.push(team);
            continue;
        }
        const abbr = bullionTeamAbbreviation(team);
        if (bullionAbbrevAppearsAsDistinctToken(combined, abbr)) {
            keys.add(nk);
            out.push(team);
        }
    }
    return out;
}

/** Trader cricket `market-display.jsx`: Tie or Draw runner on 3-way Match Winner. */

type BullionSlipLeaderCrest = { kind: "team"; name: string };

/**
 * Team crests for the slip leader column, plus any crests implied by teams named in `marketName` (deduped).
 * When the selection is Tie, returns [] — the slip row renders the draw icon instead (no team/player marks).
 */
export function bullionSlipLeaderCrests(
    runnerName: string,
    marketName: string,
    side: ReturnType<typeof inferSelectionSide>,
    teamA: string,
    teamB: string
): BullionSlipLeaderCrest[] {
    if (isBullionMarketRunnerTie(runnerName)) {
        return [];
    }
    const crests: BullionSlipLeaderCrest[] = [];
    const seenTeam = new Set<string>();

    {
        const tn = side === "home" ? teamA : side === "away" ? teamB : null;
        if (tn != null && normalizeTeamKey(tn)) {
            seenTeam.add(normalizeTeamKey(tn));
            crests.push({ kind: "team", name: tn });
        }
    }

    for (const team of teamsMentionedInBullionSlipContext(marketName, runnerName, teamA, teamB)) {
        const nk = normalizeTeamKey(team);
        if (!nk || seenTeam.has(nk)) continue;
        seenTeam.add(nk);
        crests.push({ kind: "team", name: team });
    }

    return crests;
}

function replaceTeamsWithAbbreviationsInBullionMarketName(marketName: string, teams: string[]): string {
    let out = String(marketName || "");
    for (const team of teams) {
        const full = String(team || "").trim();
        if (!full) continue;
        const abbr = bullionTeamAbbreviation(full);
        if (!abbr) continue;
        const escaped = full.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        out = out.replace(new RegExp(escaped, "gi"), abbr);
    }
    return out;
}

/**
 * Line markets often embed the handicap/total in the market title (e.g. "India Innings Runs Over 110.5").
 * Put the base market in the primary column and "Over 110.5" / "Under 250.5" in the selection column.
 */
export function splitBullionLineMarketForSlipRow(
    marketName: string,
    runnerName: string,
    teamA: string,
    teamB: string
): { primary: string; secondary: string } {
    const mn = replaceTeamsWithAbbreviationsInBullionMarketName(marketName, [teamA, teamB]).trim();
    const rn = replaceTeamsWithAbbreviationsInBullionMarketName(runnerName, [teamA, teamB]).trim();
    const re = /\s+(Over|Under)\s+([\d]+\.?[\d]*)\s*$/i;
    const match = mn.match(re);

    if (isBullionMarketRunnerTie(runnerName)) {
        if (match) {
            const base = mn.slice(0, match.index).trim();
            const line = `${match[1]} ${match[2]}`.replace(/\s+/g, " ").trim();
            if (base) return { primary: base, secondary: "Tie" };
            return { primary: mn, secondary: "Tie" };
        }
        return { primary: mn, secondary: "Tie" };
    }

    if (match) {
        const base = mn.slice(0, match.index).trim();
        const line = `${match[1]} ${match[2]}`.replace(/\s+/g, " ").trim();
        if (base) {
            const rLow = rn.toLowerCase();
            if (!rn || rLow === "over" || rLow === "under") {
                return { primary: base, secondary: line };
            }
            return { primary: base, secondary: `${line} · ${rn}` };
        }
        return { primary: mn, secondary: line };
    }
    return { primary: mn, secondary: rn };
}

export function playerInitials(name: string): string {
    const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (parts.length === 0) return "?";
    const first = parts[0]?.[0] || "";
    const second = parts.length > 1 ? parts[1]?.[0] || "" : "";
    return (first + second).toUpperCase();
}

/**
 * True once anyone has earned points or settled a pick line.
 * Until then POS stays "-" and prize-coin stacks stay hidden — potential-only ordering is not a real standing.
 */
export function bullionLeaderboardScoringStarted(
    rows: ReadonlyArray<{ points?: number | null; settledMarkets?: number | null }>,
): boolean {
    return rows.some((row) => {
        if (Math.round(Number(row.points) || 0) > 0) return true;
        if (Math.round(Number(row.settledMarkets) || 0) > 0) return true;
        return false;
    });
}

/**
 * Competition display rank from the API (points desc, then potential points desc).
 * Hidden while pre-match / before anyone has scored — the board still sorts by potential, but POS is "-".
 */
export function bullionLeaderboardDisplayRank({
    rank,
    isPreMatch,
}: {
    rank: number | null | undefined;
    /** Kept for call-site compatibility; scoring gate is applied by callers via `isPreMatch`. */
    points?: number | null | undefined;
    /** When true (pre-match or no scoring yet), POS shows "-" instead of a standing. */
    isPreMatch?: boolean;
}): number {
    if (isPreMatch) return 0;
    const rankInt = Math.round(Number(rank) || 0);
    return rankInt > 0 ? rankInt : 0;
}

/** Leaderboard name cell — prefer the signed-in account name on the viewer's own row. */
export function bullionLeaderboardRowPlayerLabel(
    row: { playerName: string; userId: number },
    options: { isCurrentUser: boolean; viewerPlayerName?: string | null },
): string {
    const rowName = String(row.playerName || "").trim();
    if (options.isCurrentUser) {
        const viewerName = String(options.viewerPlayerName || "").trim();
        if (viewerName) return viewerName;
    }
    return rowName || `Player ${row.userId}`;
}
